import { Component, OnInit, OnDestroy, TemplateRef, Input, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ContactApiService,
  IContactReminder,
  IContactRemindersFilter,
  ICreateReminderPayload,
  IReminderActionPayload,
  IReminderSummary,
  ReminderPreset,
  ReminderStatus,
  ReminderActionType,
  IReminderUser
} from '@mavles/contacts';
import { AlertService } from '@mavles/shared';
import { AppService, UserApiService, AccountApiService, User, IUser } from '@mavles/user';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface PresetTabItem {
  id: ReminderPreset;
  label: string;
  badgeKey: keyof IReminderSummary;
  badgeClass: string;
  icon: string;
}

@Component({
  selector: 'app-reminders-hub',
  templateUrl: './reminders-hub.component.html',
  styleUrls: ['./reminders-hub.component.scss'],
  standalone: false
})
export class RemindersHubComponent implements OnInit, OnDestroy {
  @Input() showHeaderCard = true;
  @Input() initialPreset: ReminderPreset = 'today';

  private contactService = inject(ContactApiService);
  private alertService = inject(AlertService);
  private appService = inject(AppService);
  private userApiService = inject(UserApiService);
  private accountService = inject(AccountApiService);
  private modalService = inject(NgbModal);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  // Live Summary Counters
  summary: IReminderSummary = {
    total_pending: 0,
    overdue: 0,
    today: 0,
    upcoming_7_days: 0,
    completed: 0
  };
  summaryLoading = false;

  // Tab Presets
  activePreset: ReminderPreset = 'today';
  presetTabs: PresetTabItem[] = [
    {
      id: 'overdue',
      label: 'Overdue',
      badgeKey: 'overdue',
      badgeClass: 'badge-danger-glow',
      icon: 'bi bi-exclamation-octagon-fill'
    },
    {
      id: 'today',
      label: 'Due Today',
      badgeKey: 'today',
      badgeClass: 'badge-warning-glow',
      icon: 'bi bi-calendar-event-fill'
    },
    {
      id: 'upcoming',
      label: 'Upcoming 7 Days',
      badgeKey: 'upcoming_7_days',
      badgeClass: 'badge-info-glow',
      icon: 'bi bi-calendar2-week-fill'
    },
    {
      id: 'completed',
      label: 'Completed',
      badgeKey: 'completed',
      badgeClass: 'badge-success-glow',
      icon: 'bi bi-check-circle-fill'
    },
    {
      id: '',
      label: 'All Pending',
      badgeKey: 'total_pending',
      badgeClass: 'badge-secondary-glow',
      icon: 'bi bi-hourglass-split'
    }
  ];

  // Reminders List & Pagination
  reminders: IContactReminder[] = [];
  loading = false;
  errorMessage = '';
  currentPage = 1;
  pageSize = 20;
  totalRecords = 0;
  lastPage = 1;

  // Filter State
  selectedAssignee: number | null = null;
  fromDate = '';
  toDate = '';
  searchQuery = '';
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  // Users List & Permissions
  usersList: (User | IUser)[] = [];
  currentUserId: number | null = null;
  isAdmin = false;
  isSupervisor = false;
  canFilterAssignee = false;

  // Create Modal State
  formTitle = '';
  formDueAt = '';
  formDescription = '';
  formContactId: number | null = null;
  formAssignedTo: number | null = null;
  createSubmitting = false;
  createErrorMessage = '';

  // Action Modal State (Complete, Reschedule, Cancel)
  selectedReminder: IContactReminder | null = null;
  actionType: ReminderActionType = 'complete';
  actionComment = '';
  actionOutcome = 'Deal Closed';
  actionNextFollowupAt = '';
  actionSubmitting = false;
  actionErrorMessage = '';

  outcomeOptions: string[] = [
    'Deal Closed',
    'Interested / Follow-up Needed',
    'Estimate Sent',
    'Meeting Completed',
    'Customer Visited',
    'Not Interested',
    'Lost to Competitor',
    'Other'
  ];

  // Action History / Detail Modal State
  historyReminder: IContactReminder | null = null;
  loadingHistory = false;

  readonly Math = Math;

  ngOnInit(): void {
    if (this.initialPreset) {
      this.activePreset = this.initialPreset;
    }

    this.checkUserRole();
    this.loadUsers();
    this.loadSummary();

    // Check query params if routed
    this.route.queryParams.subscribe((params) => {
      if (params['preset'] !== undefined) {
        this.activePreset = params['preset'] as ReminderPreset;
      }
      if (params['assigned_to']) {
        this.selectedAssignee = Number(params['assigned_to']);
      }
      if (params['from_date']) {
        this.fromDate = params['from_date'];
      }
      if (params['to_date']) {
        this.toDate = params['to_date'];
      }
      if (params['page']) {
        this.currentPage = Number(params['page']);
      }
      this.loadReminders();
    });

    // Search debounce setup
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(350),
      distinctUntilChanged()
    ).subscribe((query) => {
      this.searchQuery = query;
      this.currentPage = 1;
      this.loadReminders();
    });
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  checkUserRole(): void {
    this.isAdmin = this.appService.isAdminLoggedIn();
    const activeUser = (this.appService.currentUser || this.appService.getActiveUser()) as any;
    if (activeUser?.id) {
      this.currentUserId = activeUser.id;
    }

    this.accountService.getUser().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const u = res.data as any;
          const roleNames = Array.isArray(u.roles)
            ? u.roles.map((r: any) => (typeof r === 'string' ? r : r?.name || '').toUpperCase())
            : [];
          this.isAdmin = u.is_admin === true || roleNames.includes('ADMIN');
          this.isSupervisor = roleNames.includes('SUPERVISOR') || roleNames.includes('MANAGER');
          this.canFilterAssignee = this.isAdmin || this.isSupervisor;
        }
      },
      error: () => {
        this.isAdmin = this.appService.isAdminLoggedIn();
        this.canFilterAssignee = this.isAdmin;
      }
    });
  }

  loadUsers(): void {
    this.userApiService.getAllUsers().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.usersList = res.data;
        } else {
          this.usersList = this.appService.getAllUsers() || [];
        }
      },
      error: () => {
        this.usersList = this.appService.getAllUsers() || [];
      }
    });
  }

  loadSummary(): void {
    this.summaryLoading = true;
    this.contactService.getReminderSummary().subscribe({
      next: (res) => {
        this.summaryLoading = false;
        if (res.success && res.data) {
          this.summary = res.data;
        }
      },
      error: (err) => {
        this.summaryLoading = false;
        console.error('Failed to load reminder summary:', err);
      }
    });
  }

  loadReminders(): void {
    this.loading = true;
    this.errorMessage = '';

    const params: IContactRemindersFilter = {
      page: this.currentPage,
      page_size: this.pageSize
    };

    if (this.activePreset) {
      params.preset = this.activePreset;
    } else {
      // 'All Pending' tab
      params.reminder_status = 'pending';
    }

    if (this.selectedAssignee) {
      params.assigned_to = this.selectedAssignee;
    }

    if (this.fromDate) {
      params.from_date = this.fromDate;
    }

    if (this.toDate) {
      params.to_date = this.toDate;
    }

    this.contactService.getReminders(params).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.success && res.data) {
          let list: IContactReminder[] = [];
          if (Array.isArray(res.data)) {
            list = res.data;
            this.totalRecords = res.data.length;
            this.lastPage = 1;
          } else if (res.data.data && Array.isArray(res.data.data)) {
            list = res.data.data;
            this.currentPage = Number(res.data.current_page) || this.currentPage || 1;
            this.pageSize = Number(res.data.per_page) || this.pageSize || 20;
            this.totalRecords = Number(res.data.total) || 0;
            this.lastPage = Number(res.data.last_page) || 1;
          } else {
            list = [];
            this.totalRecords = 0;
            this.lastPage = 1;
          }

          // If local search filter is applied
          if (this.searchQuery?.trim()) {
            const q = this.searchQuery.toLowerCase().trim();
            list = list.filter((r) => {
              const titleMatch = r.title?.toLowerCase().includes(q);
              const descMatch = r.description?.toLowerCase().includes(q);
              const contactMatch = r.contact?.name?.toLowerCase().includes(q) || r.contact?.phone?.includes(q);
              const repMatch = (r.assigned_user?.firstname + ' ' + (r.assigned_user?.lastname || '')).toLowerCase().includes(q);
              return titleMatch || descMatch || contactMatch || repMatch;
            });
          }

          this.reminders = list;
        } else {
          this.reminders = [];
          this.totalRecords = 0;
          this.lastPage = 1;
        }
      },
      error: (err) => {
        this.loading = false;
        this.reminders = [];
        this.errorMessage = err?.error?.message || err?.message || 'Failed to retrieve reminders.';
      }
    });
  }

  // =========================================================================
  // Tab & Filter Handlers
  // =========================================================================

  selectPreset(preset: ReminderPreset): void {
    if (this.activePreset === preset) return;
    this.activePreset = preset;
    this.currentPage = 1;
    this.loadReminders();
  }

  onAssigneeChange(assigneeId: number | null): void {
    this.selectedAssignee = assigneeId ? Number(assigneeId) : null;
    this.currentPage = 1;
    this.loadReminders();
  }

  onDateFilterChange(): void {
    this.currentPage = 1;
    this.loadReminders();
  }

  onSearch(term: string): void {
    this.searchSubject.next(term);
  }

  clearFilters(): void {
    this.selectedAssignee = null;
    this.fromDate = '';
    this.toDate = '';
    this.searchQuery = '';
    this.currentPage = 1;
    this.loadReminders();
  }

  onPageChange(page: number): void {
    if (this.currentPage === page) return;
    this.currentPage = page;
    this.loadReminders();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = Number(size) || 20;
    this.currentPage = 1;
    this.loadReminders();
  }

  // =========================================================================
  // New Reminder Modal
  // =========================================================================

  openCreateModal(content: TemplateRef<any>): void {
    this.formTitle = '';
    this.formDescription = '';
    this.formContactId = null;
    this.formDueAt = this.getDefaultDueDateTime();
    this.formAssignedTo = this.currentUserId;
    this.createErrorMessage = '';
    this.createSubmitting = false;

    this.modalService.open(content, { centered: true, size: 'lg' });
  }

  submitCreate(modal: any): void {
    if (!this.formTitle.trim()) {
      this.createErrorMessage = 'Please enter a reminder title.';
      return;
    }
    if (!this.formDueAt) {
      this.createErrorMessage = 'Please specify due date and time.';
      return;
    }

    this.createSubmitting = true;
    this.createErrorMessage = '';

    const payload: ICreateReminderPayload = {
      title: this.formTitle.trim(),
      due_at: this.formatDateTimeForApi(this.formDueAt),
      description: this.formDescription.trim() || undefined,
      contact_id: this.formContactId ? Number(this.formContactId) : undefined,
      assigned_to: this.formAssignedTo ? Number(this.formAssignedTo) : undefined
    };

    this.contactService.createReminder(payload).subscribe({
      next: (res) => {
        this.createSubmitting = false;
        modal.close();
        this.alertService.success(res?.message || 'New follow-up reminder scheduled successfully!');
        this.loadSummary();
        this.loadReminders();
      },
      error: (err) => {
        this.createSubmitting = false;
        const msg = err?.error?.message || err?.message || 'Failed to create reminder.';
        this.createErrorMessage = msg;
        this.alertService.error(msg);
      }
    });
  }

  // =========================================================================
  // Action Modals (Complete, Reschedule, Cancel)
  // =========================================================================

  openActionModal(content: TemplateRef<any>, reminder: IContactReminder, type: ReminderActionType): void {
    this.selectedReminder = reminder;
    this.actionType = type;
    this.actionComment = '';
    this.actionOutcome = 'Deal Closed';
    this.actionNextFollowupAt = this.getDefaultDueDateTime(2);
    this.actionErrorMessage = '';
    this.actionSubmitting = false;

    this.modalService.open(content, { centered: true });
  }

  submitAction(modal: any): void {
    if (!this.selectedReminder) return;

    if (!this.actionComment.trim()) {
      this.actionErrorMessage = 'Please enter comments / notes for this action.';
      return;
    }

    if (this.actionType === 'complete' && !this.actionOutcome?.trim()) {
      this.actionErrorMessage = 'Please select or enter an outcome.';
      return;
    }

    if (this.actionType === 'reschedule' && !this.actionNextFollowupAt) {
      this.actionErrorMessage = 'Please select the new follow-up date and time.';
      return;
    }

    this.actionSubmitting = true;
    this.actionErrorMessage = '';

    const payload: IReminderActionPayload = {
      action_type: this.actionType,
      comment: this.actionComment.trim(),
      outcome: this.actionType === 'complete' ? this.actionOutcome : undefined,
      next_followup_at: this.actionType === 'reschedule' ? this.formatDateTimeForApi(this.actionNextFollowupAt) : undefined
    };

    this.contactService.recordReminderAction(this.selectedReminder.reminder_id, payload).subscribe({
      next: (res) => {
        this.actionSubmitting = false;
        modal.close();
        this.alertService.success(res?.message || `Follow-up ${this.actionType} action recorded!`);
        this.loadSummary();
        this.loadReminders();
      },
      error: (err) => {
        this.actionSubmitting = false;
        const msg = err?.error?.message || err?.message || 'Failed to record action.';
        this.actionErrorMessage = msg;
        this.alertService.error(msg);
      }
    });
  }

  // =========================================================================
  // History Modal
  // =========================================================================

  openHistoryModal(content: TemplateRef<any>, reminder: IContactReminder): void {
    this.historyReminder = reminder;
    this.loadingHistory = true;

    this.modalService.open(content, { centered: true, size: 'lg' });

    this.contactService.getReminder(reminder.reminder_id).subscribe({
      next: (res) => {
        this.loadingHistory = false;
        if (res.success && res.data) {
          this.historyReminder = res.data;
        }
      },
      error: () => {
        this.loadingHistory = false;
      }
    });
  }

  // =========================================================================
  // Presentation Helpers
  // =========================================================================

  isOverdue(dueAt: string, status: string): boolean {
    if (status !== 'pending' || !dueAt) return false;
    return new Date(dueAt).getTime() < new Date().getTime();
  }

  isDueToday(dueAt: string, status: string): boolean {
    if (status !== 'pending' || !dueAt) return false;
    const due = new Date(dueAt);
    const now = new Date();
    return due.toDateString() === now.toDateString();
  }

  getRepName(user?: IReminderUser): string {
    if (!user) return 'Unassigned';
    return `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.email || 'Rep';
  }

  getInitials(name?: string): string {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  getContactTypeBadgeClass(typeCode?: string): string {
    switch (typeCode?.toLowerCase()) {
      case 'lead':
        return 'bg-info-subtle text-info border border-info-subtle';
      case 'customer':
        return 'bg-primary-subtle text-primary border border-primary-subtle';
      case 'vendor':
        return 'bg-warning-subtle text-warning-emphasis border border-warning-subtle';
      default:
        return 'bg-secondary-subtle text-secondary border border-secondary-subtle';
    }
  }

  formatDateTimeForApi(dt: string): string {
    if (!dt) return '';
    const cleaned = dt.replace('T', ' ');
    if (cleaned.length === 16) {
      return `${cleaned}:00`;
    }
    return cleaned;
  }

  getDefaultDueDateTime(daysAhead = 0): string {
    const d = new Date();
    d.setDate(d.getDate() + daysAhead);
    d.setMinutes(d.getMinutes() + 60);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
}
